<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.promoter.hero.title')"
      :paragraph="t('jc.view.promoter.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.promoter.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.promoter.hero.float.alt'),
      }"
      :label="t('jc.view.promoter.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.promoter.ourOffer.title')"
      :paragraph="t('jc.view.promoter.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.promoter.ourConditions.title')"
      :paragraph="t('jc.view.promoter.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.promoter.yourTasks.title')"
      :paragraph="t('jc.view.promoter.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.promoter.impressions.title')"
      :paragraph="t('jc.view.promoter.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.promoter.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.promoter.howToApply.title')"
      :paragraph="t('jc.view.promoter.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.promoter.moreInformation.title')"
        :paragraph="t('jc.view.promoter.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/pr_manager.png'

definePageMeta({
  nameKey: 'participate-promoter',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.promoter.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'fingerprint',
    text: t('jc.view.promoter.ourOffer.iconHighlight.corporateIdentity.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.promoter.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: { prefix: 'fab', iconName: 'bluesky' },
    text: t('jc.view.promoter.ourOffer.iconHighlight.socialMedia.text'),
  },
  {
    icon: 'file-certificate',
    text: t('jc.view.promoter.ourOffer.iconHighlight.licences.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.promoter.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.promoter.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.promoter.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.promoter.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.promoter.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.promoter.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'paintbrush-pencil',
    text: t('jc.view.promoter.ourOffer.icon.passionatePeople.text'),
  },
  {
    icon: 'industry-windows',
    text: t('jc.view.promoter.ourOffer.icon.regularContent.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.promoter.ourOffer.icon.exchange.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.promoter.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'handshake',
    text: t('jc.view.promoter.ourOffer.icon.closeCooperation.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.promoter.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'chart-line-up',
    text: t('jc.view.promoter.ourConditions.icon.willpower.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.promoter.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.promoter.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.promoter.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.promoter.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'pen',
    text: t('jc.view.promoter.yourTasks.icon.write.text'),
  },
  {
    icon: 'sparkles',
    text: t('jc.view.promoter.yourTasks.icon.prettify.text'),
  },
  {
    icon: 'stamp',
    text: t('jc.view.promoter.yourTasks.icon.shape.text'),
  },
  {
    icon: 'users',
    text: t('jc.view.promoter.yourTasks.icon.design.text'),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.promoter.yourTasks.icon.organise.text'),
  },
  {
    icon: 'chart-line-up',
    text: t('jc.view.promoter.yourTasks.icon.think.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.promoter.howToApply.icon.firstContact.title'),
    text: t('jc.view.promoter.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.promoter.howToApply.icon.conversation.title'),
    text: t('jc.view.promoter.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.promoter.howToApply.icon.wait.title'),
    text: t('jc.view.promoter.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.promoter.howToApply.icon.trainee.title'),
    text: t('jc.view.promoter.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.promoter.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.promoter.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.promoter.moreInformation.card.team.text'),
    label: t('jc.view.promoter.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.promoter.moreInformation.card.weekly_review.text'),
    label: t('jc.view.promoter.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.promoter.moreInformation.card.plan.text'),
    label: t('jc.view.promoter.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.promoter.moreInformation.card.stackShare.text'),
    label: t('jc.view.promoter.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.promoter.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.promoter.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.promoter.moreInformation.card.blog.text'),
    label: t('jc.view.promoter.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
